import { useEffect, useState } from "react";
import { X, Phone, Sparkles } from "lucide-react";
import { useAppSettings } from "@/lib/appSettings";

const SESSION_KEY = "odia_popup_seen_v1";

// Hardcoded fallback so first paint (before /app/settings resolves) and
// any stale-cache scenario still shows a readable card. The admin can
// override every field via /admin/app/settings → teachersMessageContent.
const FALLBACK = {
  title: "ବିଲ୍‌ ଏବଂ ଭାଉଚର ପ୍ରସ୍ତୁତ କରିବା ଚିନ୍ତା ଏବେ ଆମର! 📑✅",
  greeting: "ନମସ୍କାର ଶିକ୍ଷକ ବନ୍ଧୁଗଣ, 🙏",
  intro: "ଆପଣଙ୍କୁ କ’ଣ ଅନଲାଇନ୍ ଟେକ୍ନୋଲୋଜି ବା ୱେବସାଇଟ୍ ବ୍ୟବହାର କରିବାରେ ଅସୁବିଧା ହେଉଛି? ଜମାରୁ ବ୍ୟସ୍ତ ହୁଅନ୍ତୁ ନାହିଁ! ଆମେ ବୁଝୁଛୁ ଯେ ଜଣେ ଶିକ୍ଷକ ଭାବରେ ଆପଣଙ୍କ ପାଠପଢ଼ା ଦାୟିତ୍ୱ ସବୁଠାରୁ ଗୁରୁତ୍ୱପୂର୍ଣ୍ଣ, ଆଉ ଏହି ଜଟିଳ କାଗଜପତ୍ର କାମ ଆପଣଙ୍କ ପାଇଁ ଏକ ଅତିରିକ୍ତ ବୋଝ।",
  offerHead: "ଯଦି ଆପଣ ଟେକ୍ନୋଲୋଜିରେ ଅସମର୍ଥ, ତେବେ ଏହି ସୁବିଧା କେବଳ ଆପଣଙ୍କ ପାଇଁ:",
  offerBody: "ଆପଣଙ୍କୁ ନିଜେ କିଛି କରିବାକୁ ପଡ଼ିବ ନାହିଁ! କେବଳ ପ୍ରତି ମାସ ଶେଷରେ ଆପଣଙ୍କ ବିଦ୍ୟାଳୟର (Balvatika, Primary, Upper Primary, High School) Daily Enrollment Data ଯାହା ଆପଣ ନିଜ ସ୍କୁଲ୍ ରେକର୍ଡରେ Date-wise ଲେଖିଛନ୍ତି, ତାହାର ଏକ ଫଟୋ ଆମକୁ ପଠାଇ ଦିଅନ୍ତୁ।",
  whatHead: "ଆମେ ଆପଣଙ୍କ ପାଇଁ କ’ଣ କରିବୁ?",
  benefits: [
    "✅ ଆମ ଏକ୍ସପର୍ଟ ଟିମ୍ ଆପଣଙ୍କର ସମସ୍ତ Bill, Voucher ଏବଂ UC ସମ୍ପୂର୍ଣ୍ଣ ସଠିକ ଭାବେ ପ୍ରସ୍ତୁତ କରିବେ।",
    "✅ ସବୁକିଛି ରେଡି ହେବା ପରେ ଆମେ ଆପଣଙ୍କୁ ସିଧାସଳଖ WhatsApp ମାଧ୍ୟମରେ ପଠାଇ ଦେବୁ।",
    "✅ ଆପଣ କେବଳ ତାହାକୁ ପ୍ରିଣ୍ଟ୍ କରି ଅଫିସ୍‌ରେ ଦାଖଲ କରିବେ।",
  ],
  negHead: "ବିଶେଷତ୍ୱ:",
  negs: [
    "❌ କୌଣସି ଭୁଲ୍ ହେବାର ଭୟ ନାହିଁ।",
    "❌ କମ୍ପ୍ୟୁଟର ବା ୱେବସାଇଟ୍ ଚଲାଇବା ଦରକାର ନାହିଁ।",
    "❌ ମାସ ଶେଷରେ ଅଫିସ୍ Reminder ଚିଠିର ଡର ନାହିଁ।",
  ],
  cta: "ଆଜି ହିଁ ନିଶ୍ଚିନ୍ତ ହୋଇଯାଆନ୍ତୁ! ଆପଣଙ୍କର ଡାଟା ପଠାଇବା ପାଇଁ ବା ଅଧିକ ଜାଣିବା ପାଇଁ ଏହି ନମ୍ବରରେ WhatsApp କରନ୍ତୁ:",
  waNumber: "918093414633",
};

function useTeachersMessage() {
  const s = useAppSettings();
  const enabled = s.teachersMessageEnabled !== false; // default ON
  const c = s.teachersMessageContent || {};
  const content = {
    title: c.title || FALLBACK.title,
    greeting: c.greeting || FALLBACK.greeting,
    intro: c.intro || FALLBACK.intro,
    offerHead: c.offerHead || FALLBACK.offerHead,
    offerBody: c.offerBody || FALLBACK.offerBody,
    whatHead: c.whatHead || FALLBACK.whatHead,
    benefits: Array.isArray(c.benefits) && c.benefits.length ? c.benefits : FALLBACK.benefits,
    negHead: c.negHead || FALLBACK.negHead,
    negs: Array.isArray(c.negs) && c.negs.length ? c.negs : FALLBACK.negs,
    cta: c.cta || FALLBACK.cta,
    waNumber: (c.waNumber || FALLBACK.waNumber).replace(/\D/g, ""),
  };
  return { enabled, content };
}

function Content({ compact = false, content }) {
  const waPretty = content.waNumber.startsWith("91")
    ? `+91 ${content.waNumber.slice(2, 7)} ${content.waNumber.slice(7)}`
    : content.waNumber;
  return (
    <div className={compact ? "text-[0.82rem] leading-relaxed sm:text-sm" : "text-sm sm:text-base"}>
      <p className="break-words font-heading text-base font-bold leading-tight text-ink-900 sm:text-xl lg:text-2xl">{content.title}</p>
      <p className="mt-2 font-semibold text-ink-900 sm:mt-3">{content.greeting}</p>
      <p className="mt-2 leading-relaxed text-ink-700">{content.intro}</p>

      <p className="mt-3 font-bold text-clay sm:mt-4">{content.offerHead}</p>
      <p className="mt-1 leading-relaxed text-ink-700">{content.offerBody}</p>

      <p className="mt-3 font-bold text-clay sm:mt-4">{content.whatHead}</p>
      <ul className="mt-1.5 space-y-1.5 sm:mt-2">
        {content.benefits.map((b, i) => (<li key={`b-${i}`} className="leading-relaxed text-ink-900">{b}</li>))}
      </ul>

      <p className="mt-3 font-bold text-clay sm:mt-4">{content.negHead}</p>
      <ul className="mt-1.5 space-y-1.5 sm:mt-2">
        {content.negs.map((b, i) => (<li key={`n-${i}`} className="leading-relaxed text-ink-900">{b}</li>))}
      </ul>

      <p className="mt-3 font-semibold text-ink-900 sm:mt-4">{content.cta}</p>
      <a
        href={`https://wa.me/${content.waNumber}`}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex max-w-full flex-wrap items-center justify-center gap-1.5 rounded-full bg-moss px-3 py-2 text-[0.78rem] font-bold text-white shadow-[0_8px_18px_-8px_rgba(116,134,80,0.55)] active:scale-[0.99] hover:bg-moss-dark sm:gap-2 sm:px-4 sm:text-sm"
        data-testid="odia-message-whatsapp-btn"
      >
        <Phone className="h-4 w-4 shrink-0" />
        <span className="break-words">📲 {waPretty}</span>
      </a>
    </div>
  );
}

// Card variant shown ABOVE quick links on the dashboard.
export function OdiaMessageCard() {
  const { enabled, content } = useTeachersMessage();
  if (!enabled) return null;
  return (
    <section
      className="rounded-2xl border-2 border-clay/25 bg-gradient-to-br from-clay/5 via-white to-moss/5 p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-7"
      data-testid="odia-message-card"
    >
      <div className="flex items-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-clay text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <span className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-clay">For our teachers</span>
      </div>
      <div className="mt-4">
        <Content content={content} />
      </div>
    </section>
  );
}

// Popup variant — shows once per browser session.
export function OdiaMessagePopup() {
  const { enabled, content } = useTeachersMessage();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    try {
      // iter161 — skip on a Toolkit auto-login arrival; shows on the next normal visit.
      if (!sessionStorage.getItem(SESSION_KEY) && !sessionStorage.getItem("mdm.ssoArrival")) {
        setOpen(true);
      }
    } catch (_e) { /* sessionStorage may be disabled */ }
  }, []);

  if (!open || !enabled) return null;
  const close = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (_e) { /* ignore */ }
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-4" data-testid="odia-message-popup">
      <button
        aria-label="Close"
        onClick={close}
        className="absolute inset-0 bg-ink-900/55 backdrop-blur-sm animate-in fade-in duration-200"
      />
      <div className="relative flex max-h-[92dvh] w-full flex-col rounded-t-3xl bg-white shadow-2xl animate-in slide-in-from-bottom duration-300 sm:max-h-[88vh] sm:max-w-xl sm:rounded-2xl">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-2 top-2 z-10 grid h-8 w-8 place-items-center rounded-full bg-sand-100 text-ink-700 hover:bg-sand-200 sm:right-3 sm:top-3 sm:h-9 sm:w-9"
          data-testid="odia-message-popup-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex-1 overflow-y-auto px-4 pb-3 pt-5 sm:px-7 sm:pb-4 sm:pt-7">
          <Content compact content={content} />
        </div>
        <div className="shrink-0 border-t border-border bg-white px-4 py-3 sm:px-7 sm:py-4">
          <button
            type="button"
            onClick={close}
            className="w-full rounded-2xl border border-border bg-white py-2.5 text-sm font-semibold text-ink-700 hover:bg-sand-50"
            data-testid="odia-message-popup-dismiss"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
